import React, { useState } from 'react';
import {
  Avatar,
  Button,
  Dropdown,
  Form,
  Input,
  Menu,
  Message,
  Modal,
  Space,
} from '@arco-design/web-react';
import {
  IconDown,
  IconPoweroff,
  IconSettings,
  IconUser,
} from '@arco-design/web-react/icon';
import useLocale from '@/utils/useLocale';
import Logo from '@/assets/logo.svg';
import styles from './style/index.module.less';
import useStorage from '@/utils/useStorage';
import { useHistory } from 'react-router-dom';
import packageJson from '../../../package.json';
import { DataSync } from '@/services/data';
import { changePassword, generateApiKey } from '@/services/user';

async function writeClipboardText(text: string) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.left = '-9999px';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);

  try {
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, text.length);
    if (!document.execCommand('copy')) {
      throw new Error('copy command failed');
    }
  } finally {
    document.body.removeChild(textArea);
  }
}

function validatePassword(value: string, callback: (message?: string) => void) {
  if (!value) {
    callback();
  } else if (Array.from(value).length < 6) {
    callback('密码至少 6 个字符');
  } else if (!value.trim()) {
    callback('密码不能全部为空白字符');
  } else if (new TextEncoder().encode(value).length > 72) {
    callback('密码最多 72 字节，中文和 Emoji 会占用多个字节');
  } else {
    callback();
  }
}

function Navbar() {
  const t = useLocale();
  const history = useHistory();
  const [, , removeUserStatus] = useStorage('userStatus');
  const [username, , removeUsername] = useStorage('username');
  const [, , removeToken] = useStorage('token');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [passwordForm] = Form.useForm();

  function logout() {
    removeUserStatus();
    removeUsername();
    removeToken();
    let loginPath = '/login';
    const currentPath = window.location.pathname;
    currentPath !== '/' && (loginPath += `?redirect=${encodeURI(currentPath)}`);
    window.location.href = loginPath;
  }

  function onMenuItemClick(key: string) {
    if (key === 'logout') {
      logout();
    } else if (key === 'password') {
      setPasswordVisible(true);
    } else if (key === 'api-key') {
      setApiKey('');
      setApiKeyVisible(true);
    } else {
      Message.info(`You clicked ${key}`);
    }
  }

  function modalConfirm() {
    Modal.confirm({
      title: '提示',
      content: '确认需要同步数据吗？',
      onOk: () =>
        new Promise((resolve, reject) => {
          DataSync()
            .then(() => {
              Message.success('同步成功');
              resolve(true);
            })
            .catch(() => reject(false));
        }),
    });
  }

  async function submitChangePassword() {
    try {
      const values = await passwordForm.validate();
      setPasswordLoading(true);
      await changePassword({
        current_password: values.current_password,
        new_password: values.new_password,
      });
      Message.success('密码修改成功，请使用新密码重新登录');
      setPasswordVisible(false);
      passwordForm.resetFields();
      window.setTimeout(logout, 800);
    } catch (e) {
      console.log(e);
    } finally {
      setPasswordLoading(false);
    }
  }

  async function submitGenerateApiKey() {
    try {
      setApiKeyLoading(true);
      const result = await generateApiKey();
      setApiKey(result.api_key);
      Message.success('API Key 已生成，旧 Key 已失效');
    } catch (e) {
      console.log(e);
    } finally {
      setApiKeyLoading(false);
    }
  }

  async function copyApiKey() {
    try {
      await writeClipboardText(apiKey);
      Message.success('API Key 已复制');
    } catch (e) {
      Message.error('复制失败，请手动复制');
    }
  }

  function closeApiKeyModal() {
    setApiKey('');
    setApiKeyVisible(false);
  }

  const droplist = (
    <Menu onClickMenuItem={onMenuItemClick}>
      <Menu.Item key="password">
        <IconSettings className={styles['dropdown-icon']} />
        修改密码
      </Menu.Item>
      <Menu.Item key="api-key">生成 API Key</Menu.Item>
      <Menu.Item key="logout">
        <IconPoweroff className={styles['dropdown-icon']} />
        {t['navbar.logout']}
      </Menu.Item>
    </Menu>
  );

  return (
    <div className={styles.navbar}>
      <div className={styles.left}>
        <div className={styles.logo} onClick={() => history.push('/')}>
          <Logo />
          <div className={styles['logo-name']}>{packageJson.productName}</div>
        </div>
      </div>
      <ul className={styles.right}>
        {/*<li>*/}
        {/*  <Button size="small" type="secondary" onClick={modalConfirm}>*/}
        {/*    数据同步*/}
        {/*  </Button>*/}
        {/*</li>*/}
        <li>
          <Dropdown droplist={droplist} position="bottom">
            <Space align="center" size="mini" style={{ cursor: 'pointer' }}>
              <Avatar size={28} style={{ cursor: 'pointer' }}>
                <IconUser />
              </Avatar>
              <span className={styles.username}>{username}</span>
              <IconDown className={styles['dropdown-icon']} />
            </Space>
          </Dropdown>
        </li>
      </ul>
      <Modal
        title="修改密码"
        visible={passwordVisible}
        onOk={submitChangePassword}
        confirmLoading={passwordLoading}
        onCancel={() => {
          setPasswordVisible(false);
          passwordForm.resetFields();
        }}
      >
        <Form form={passwordForm}>
          <Form.Item
            label="当前密码"
            field="current_password"
            rules={[{ required: true, message: '请输入当前密码' }]}
          >
            <Input.Password onPressEnter={submitChangePassword} />
          </Form.Item>
          <Form.Item
            label="新密码"
            field="new_password"
            rules={[
              { required: true, message: '请输入新密码' },
              { validator: validatePassword },
            ]}
          >
            <Input.Password onPressEnter={submitChangePassword} />
          </Form.Item>
          <Form.Item
            label="确认新密码"
            field="confirm_password"
            dependencies={['new_password']}
            rules={[
              { required: true, message: '请再次输入新密码' },
              {
                validator: (value, callback) => {
                  if (value !== passwordForm.getFieldValue('new_password')) {
                    callback('两次输入的新密码不一致');
                  } else {
                    callback();
                  }
                },
              },
            ]}
          >
            <Input.Password onPressEnter={submitChangePassword} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="OpenAPI Key"
        visible={apiKeyVisible}
        onCancel={closeApiKeyModal}
        footer={
          <Space>
            <Button onClick={closeApiKeyModal}>关闭</Button>
            <Button
              type="primary"
              loading={apiKeyLoading}
              onClick={submitGenerateApiKey}
            >
              {apiKey ? '重新生成' : '生成 API Key'}
            </Button>
          </Space>
        }
      >
        <p>重新生成后，之前的 API Key 会立即失效。</p>
        {apiKey ? (
          <>
            <Input.TextArea value={apiKey} readOnly autoSize />
            <Button type="text" onClick={copyApiKey}>
              复制 API Key
            </Button>
            <p>请立即保存，此 Key 关闭窗口后将不再显示。</p>
          </>
        ) : (
          <p>API Key 用于调用 /openapi 接口，请勿提交到代码仓库。</p>
        )}
      </Modal>
    </div>
  );
}

export default Navbar;
